import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { UserPlus, Mail, Phone, User, Building } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AttorneyRegister = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    bar_number: "",
    state: ""
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.bar_number || !formData.state) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/register/attorney`, formData);
      
      toast.success(response.data.message);
      
      // Send Gmail notification to admin
      const adminEmail = "therecruitingpartner@gmail.com";
      const emailBody = `New Attorney Registration

Name: ${formData.name}
Email: ${formData.email}
Phone: ${formData.phone}
Bar Number: ${formData.bar_number}
State: ${formData.state}
Verification Status: ${response.data.verification_status}

---
CoverDocket Attorney Registration`;

      const subject = encodeURIComponent(`New Attorney Registration - ${formData.name}`);
      const body = encodeURIComponent(emailBody);
      const gmailLink = `https://mail.google.com/mail/?view=cm&fs=1&to=${adminEmail}&su=${subject}&body=${body}`;
      
      window.open(gmailLink, '_blank');
      
      // Reset form
      setFormData({ name: "", email: "", phone: "", bar_number: "", state: "" });
      
      // Navigate back to upload after short delay
      setTimeout(() => navigate("/upload"), 3000);
      
    } catch (error) {
      console.error("Registration error:", error);
      toast.error(error.response?.data?.detail || "Failed to register. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F5F5F5] to-[#e8f4f8]">
      {/* Header */}
      <div className="header-gradient text-white py-6 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex items-center gap-4">
          <img 
            src="/coverdocket-logo.png" 
            alt="CoverDocket Logo" 
            className="h-16 w-auto"
          />
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Join Our Coverage Network</h1>
            <p className="mt-1 text-lg opacity-90">Register as a coverage attorney</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-2xl mx-auto px-4 py-12">
        <Card className="glass-card border-0">
          <CardHeader className="space-y-2">
            <CardTitle className="text-2xl font-semibold text-[#0A1128]">
              Attorney Registration
            </CardTitle>
            <CardDescription className="text-base">
              Join CoverDocket's network of coverage attorneys. Submit your bar credentials and we'll review and contact you with opportunities.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Full Name */}
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-medium flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Full Name *
                </Label>
                <Input
                  id="name"
                  data-testid="attorney-name-input"
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Jane Smith"
                  className="h-11"
                />
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Email *
                </Label>
                <Input
                  id="email"
                  data-testid="attorney-email-input"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="your.email@example.com"
                  className="h-11"
                />
              </div>

              {/* Phone */}
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-sm font-medium flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Phone Number
                </Label>
                <Input
                  id="phone"
                  data-testid="attorney-phone-input"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="(555) 123-4567"
                  className="h-11"
                />
              </div>

              {/* Bar Number */}
              <div className="space-y-2">
                <Label htmlFor="bar_number" className="text-sm font-medium flex items-center gap-2">
                  <Building className="w-4 h-4" />
                  State Bar Number *
                </Label>
                <Input
                  id="bar_number"
                  data-testid="attorney-bar-number-input"
                  type="text"
                  required
                  value={formData.bar_number}
                  onChange={(e) => setFormData({ ...formData, bar_number: e.target.value })}
                  placeholder="e.g., 123456"
                  className="h-11"
                />
              </div>

              {/* State */}
              <div className="space-y-2">
                <Label htmlFor="state" className="text-sm font-medium">
                  State of Licensure *
                </Label>
                <Input
                  id="state"
                  data-testid="attorney-state-input"
                  type="text"
                  required
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value.toUpperCase() })}
                  placeholder="e.g., FL, CA, NY"
                  className="h-11"
                  maxLength={2}
                />
                <p className="text-xs text-gray-600">
                  Your bar credentials will be reviewed manually by our team. We'll verify FL, CA, and NY bars and contact you once approved.
                </p>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                data-testid="register-attorney-button"
                disabled={loading}
                className="w-full h-12 text-base btn-primary font-medium"
              >
                {loading ? "Registering..." : "Register as Coverage Attorney"}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <Button
                variant="link"
                onClick={() => navigate("/upload")}
                className="text-[#6497B1]"
              >
                Back to Upload Page
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AttorneyRegister;